import React from 'react';
import { Placeholder, Stack, Dropdown } from 'react-bootstrap';
import './SocialPostSkeleton.scss';

interface SocialPostSkeletonProps {
	variant?: 'simple' | 'with-image' | 'short' | 'long';
	noButton?: boolean;
}

export const SocialPostSkeleton: React.FC<SocialPostSkeletonProps> = ({
	variant = 'simple',
	noButton = false,
}) => {
	// Generate different content patterns based on variant
	const getContentLines = () => {
		switch (variant) {
			case 'short':
				return (
					<>
						<Placeholder xs={12} className="mb-2" />
						<Placeholder xs={10} className="mb-2" />
						<Placeholder xs={8} />
					</>
				);
			case 'long':
				return (
					<>
						<Placeholder xs={12} className="mb-2" />
						<Placeholder xs={12} className="mb-2" />
						<Placeholder xs={11} className="mb-2" />
						<Placeholder xs={10} className="mb-2" />
						<Placeholder xs={9} className="mb-2" />
						<Placeholder xs={7} />
					</>
				);
			case 'with-image':
				return (
					<>
						<Placeholder xs={12} className="mb-2" />
						<Placeholder xs={11} className="mb-2" />
						<Placeholder xs={10} className="mb-2" />
						<Placeholder xs={8} className="mb-3" />
						<Placeholder xs={12} style={{ height: '250px', borderRadius: '12px' }} />
					</>
				);
			default: // simple
				return (
					<>
						<Placeholder xs={12} className="mb-2" />
						<Placeholder xs={11} className="mb-2" />
						<Placeholder xs={10} className="mb-2" />
						<Placeholder xs={9} className="mb-2" />
						<Placeholder xs={7} />
					</>
				);
		}
	};

	return (
		<div className="social-post-skeleton white-box bg-white mb-3">
			<Stack className="social-post-inner">
				{/* Header Section - Profile and Actions */}
				<div className="social-post-header position-relative d-flex justify-content-between align-items-center">
					<div className="post-profile flex-1 d-flex flex-column flex-sm-row align-items-baseline align-items-sm-center gap-2 gap-xxl-3">
						{/* Profile Image */}
						<div className="post-profile-img rounded-circle">
							<Placeholder animation="glow" />
						</div>

						{/* User Info */}
						<div className="flex-grow-1">
							<Placeholder animation="glow">
								<Placeholder xs={6} size="lg" className="mb-1 fw-bold" />
							</Placeholder>
							<div className="post-profile-detail w-100 d-flex align-items-center flex-wrap">
								<Placeholder animation="glow" style={{ width: '30%' }}>
									<Placeholder xs={4} size="sm" className="me-2 w-100" />
									<Placeholder xs={5} size="sm" className="w-100" />
								</Placeholder>
							</div>
						</div>
					</div>

					{/* Actions */}
					<div className="post-profile-actions d-flex gap-2 gap-xxl-3">
						{/* Connect Button */}

						{!noButton && (
							<>
								<Placeholder.Button
									variant="primary"
									className="filled-btn rounded-pill"
									style={{ width: '80px', height: '32px' }}
								/>
								<Placeholder.Button
									variant="primary"
									className="filled-btn rounded-pill"
									style={{ width: '80px', height: '32px' }}
								/>
							</>
						)}

						{/* More Options */}
						<div className="edit-action">
							<Dropdown>
								<Dropdown.Toggle
									variant="success"
									id="dropdown-basic"
									className="p-0 rounded-circle"
								>
									<i className="ri-more-2-fill"></i>
								</Dropdown.Toggle>
							</Dropdown>
						</div>
					</div>
				</div>

				{/* Content Section */}
				<div className="social-post-body">
					<div className="post-content">
						<Placeholder animation="glow">{getContentLines()}</Placeholder>
					</div>
				</div>

				{/* Engagement Section */}
				<div className="social-post-actions d-flex gap-1 gap-sm-3">
					<div className="d-flex gap-1 gap-sm-2">
						{/* Like Button */}
						<Placeholder.Button
							variant="primary"
							className="gray-transparent-btn post-like-btn rounded-pill"
							style={{ width: '60px', height: '32px' }}
						/>

						{/* Like Count */}
						<Placeholder.Button
							variant="primary"
							className="gray-transparent-btn rounded-pill"
							style={{ width: '80px', height: '32px' }}
						/>
					</div>

					{/* Comments Button */}
					<Placeholder.Button
						variant="primary"
						className="gray-transparent-btn rounded-pill"
						style={{ width: '100px', height: '32px' }}
					/>

					{/* Share Button */}
					<Placeholder.Button
						variant="primary"
						className="gray-transparent-btn rounded-pill"
						style={{ width: '60px', height: '32px' }}
					/>
				</div>
			</Stack>
		</div>
	);
};

// Multiple skeleton items for better loading experience
export const SocialPostSkeletonList: React.FC<{ count?: number; noButton?: boolean }> = ({
	count = 3,
	noButton,
}) => {
	const variants: Array<'simple' | 'with-image' | 'short' | 'long'> = [
		'simple',
		'short',
		'long',
		'with-image',
	];

	return (
		<>
			{Array.from({ length: count }).map((_, index) => (
				<SocialPostSkeleton
					noButton={noButton}
					key={`skeleton-${index}`}
					variant={variants[index % variants.length]}
				/>
			))}
		</>
	);
};

export default SocialPostSkeleton;
